import React, { useCallback, useLayoutEffect, useRef, useState } from "react";
import { Image } from "antd";
import { mediaFrameSize } from "../../../messaging/mediaFrameSize";
import { ChatVideoPreview } from "./ChatVideoPreview";
import { BUBBLE_MAX_WIDTH, CHAT_THREAD_VIEWPORT, MEDIA_MAX_THREAD_SHARE, MEDIA_MIN_HEIGHT } from "./chatLayout";
import { usePageVisible } from "../../performance/pageVisibility";

// Shared by uploaded optimistic messages and authenticated canonical attachments.
export function ChatMediaPreview({ kind, src, width, height, alt, placeholder, imagePreview = true, onImageError, rowSelector = '.msg-row', viewportSelector = CHAT_THREAD_VIEWPORT, previewZIndex }) {
  const visible = usePageVisible();
  const [natural, setNatural] = useState(null);
  const container = useRef(null);
  const [availableWidth, setAvailableWidth] = useState(null);
  const [availableHeight, setAvailableHeight] = useState(null);
  useLayoutEffect(() => {
    const row = container.current?.closest(rowSelector);
    if (!row) return undefined;
    const viewport = row.closest(viewportSelector);
    const measure = () => {
      if (row.clientWidth > 0) setAvailableWidth(Math.max(1, Math.min(row.clientWidth * 0.8, BUBBLE_MAX_WIDTH) - 36));
      if (viewport?.clientHeight > 0) setAvailableHeight(Math.max(MEDIA_MIN_HEIGHT, viewport.clientHeight * MEDIA_MAX_THREAD_SHARE));
    };
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(row);
    if (viewport) observer.observe(viewport);
    return () => observer.disconnect();
  }, [rowSelector, viewportSelector]);
  const dimensions = natural && natural.src === src ? natural : { width, height };
  const frame = mediaFrameSize(kind, dimensions.width, dimensions.height);
  // Fit the width first, then the thread's height, keeping the proportions.
  const widthScale = availableWidth ? Math.min(1, availableWidth / frame.width) : 1;
  const scale = widthScale * (availableHeight ? Math.min(1, availableHeight / (frame.height * widthScale)) : 1);
  const targetWidth = frame.width * scale;
  const targetHeight = frame.height * scale;
  const [displayFrame, setDisplayFrame] = useState({ width: targetWidth, height: targetHeight });
  const currentFrame = useRef(displayFrame);
  useLayoutEffect(() => {
    const start = currentFrame.current;
    if (start.width === targetWidth && start.height === targetHeight) return undefined;
    const update = value => { currentFrame.current = value; setDisplayFrame(value); };
    if (!visible || window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      update({ width: targetWidth, height: targetHeight });
      return undefined;
    }
    const started = performance.now();
    let request;
    const step = now => {
      const progress = Math.max(0, Math.min(1, (now - started) / 350));
      const eased = progress * progress * (3 - 2 * progress);
      update({ width: start.width + (targetWidth - start.width) * eased, height: start.height + (targetHeight - start.height) * eased });
      if (progress < 1) request = requestAnimationFrame(step);
    };
    request = requestAnimationFrame(step);
    return () => cancelAnimationFrame(request);
  }, [targetWidth, targetHeight, visible]);
  const remember = useCallback((w, h) => {
    if (w > 0 && h > 0) setNatural({ src, width: w, height: h });
  }, [src]);
  const style = { display: "block", width: "100%", height: "100%", objectFit: kind === "sticker" ? "contain" : "cover" };
  return <div ref={container} className="canonical-chat-media-frame" data-testid="chat-media-frame" onLoadCapture={event => {
    if (kind !== "video") remember(event.target.naturalWidth, event.target.naturalHeight);
  }} style={{
    position: "relative", width: displayFrame.width, height: displayFrame.height, maxWidth: "100%", maxHeight: displayFrame.height,
    minWidth: 0, minHeight: 0, flex: "none", boxSizing: "border-box", aspectRatio: `${displayFrame.width} / ${displayFrame.height}`,
    overflow: "hidden", borderRadius: kind === "sticker" ? 0 : 6, border: 0,
    background: kind === "sticker" ? "transparent" : "rgba(0,0,0,0.12)",
  }}>
    <div style={{ position: "absolute", inset: 0, minWidth: 0, minHeight: 0, overflow: "hidden", display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gridTemplateRows: "minmax(0, 1fr)", placeItems: "center" }}>
      {!src ? placeholder : kind === "video"
        ? <ChatVideoPreview key={src} src={src} alt={alt} onDimensions={remember} />
        : kind === "sticker" || !imagePreview ? <img draggable={false} src={src} loading="lazy" decoding="async" alt={alt || (kind === "sticker" ? "Sticker" : "Photo")} style={style} onError={onImageError} />
        : <Image draggable={false} src={src} loading="lazy" decoding="async" alt={alt || "Photo"} preview={{ mask: false, ...(previewZIndex ? { zIndex: previewZIndex } : {}) }}
            width="100%" height="100%" style={{ ...style, cursor: "pointer" }} />}
    </div>
  </div>;
}
